import {
  ArrowLeftRight, Briefcase, CalendarRange, CircleHelp, Home, LineChart, PieChart, PiggyBank, Settings, Target, Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useMatches } from 'react-router';
import type { HelpKey } from '@/help';
import { useAppStore } from '@/lib/store';

/**
 * Route metadata. Every route object in `routes.tsx` sets `handle: RouteHandle`; the shell reads the deepest
 * match for the document title, breadcrumb, help article and first-run tour.
 */
export interface RouteHandle {
  readonly title: string;
  readonly helpKey: HelpKey;
  /** Key into `src/tours` — auto-runs once on first visit. */
  readonly tourId?: string;
  /** Breadcrumb label (defaults to title). Set `crumb: null` to hide from breadcrumbs. */
  readonly crumb?: string | null;
}

const isHandle = (h: unknown): h is RouteHandle =>
  typeof h === 'object' && h !== null && 'title' in h && 'helpKey' in h;

/** Matched pathname without a splat remainder (`/invest/*` on /invest/trades → /invest). */
const basePath = (pathname: string, splat: string | undefined): string =>
  splat && pathname.endsWith(`/${splat}`) ? pathname.slice(0, -splat.length - 1) || '/' : pathname;

/** All matched route handles, outermost first. */
export const useRouteHandles = (): { handle: RouteHandle; pathname: string }[] =>
  useMatches()
    .filter((m) => isHandle(m.handle))
    .map((m) => ({ handle: m.handle as RouteHandle, pathname: basePath(m.pathname, m.params['*']) }));

/** Deepest matched route handle. */
export const useRouteHandle = (): RouteHandle | undefined => {
  const all = useRouteHandles();
  return all[all.length - 1]?.handle;
};

/** Deepest route handle merged with the current page's `PageHeader` meta (title, helpKey, tourId). */
export const usePageMeta = (): RouteHandle | undefined => {
  const handle = useRouteHandle();
  const meta = useAppStore((s) => s.pageMeta);
  if (!handle) return undefined;
  return { ...handle, title: meta?.title ?? handle.title, helpKey: meta?.helpKey ?? handle.helpKey, tourId: meta?.tourId ?? handle.tourId };
};

export type NavBadge = 'unreviewed' | 'bills';
export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly to: string;
  readonly icon: LucideIcon;
  /** Shortcut shown in tooltips/palette (from keyboard.ts). */
  readonly keys?: string;
  readonly badge?: NavBadge;
  /** Show in the mobile bottom bar (max 4 + More). */
  readonly mobile?: boolean;
  /** Short purpose line (used by the palette and placeholders). */
  readonly purpose: string;
}

/** Primary navigation, in SPEC §8 order. Settings & Help are rendered separately at the sidebar foot. */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', label: 'Home', to: '/', icon: Home, keys: 'g h', mobile: true, purpose: 'Your money at a glance.' },
  { id: 'accounts', label: 'Accounts', to: '/accounts', icon: Wallet, keys: 'g a', purpose: 'Bank, card, loan, super and asset accounts with balances and reconciliation.' },
  { id: 'transactions', label: 'Transactions', to: '/transactions', icon: ArrowLeftRight, keys: 'g t', badge: 'unreviewed', mobile: true, purpose: 'A keyboard-first register with splits, transfers, rules and imports.' },
  { id: 'budget', label: 'Budget', to: '/budget', icon: PiggyBank, keys: 'g b', mobile: true, purpose: 'Jars that fill each payday, or a detailed category budget with pacing and safe-to-spend.' },
  { id: 'goals', label: 'Goals', to: '/goals', icon: Target, keys: 'g g', mobile: true, purpose: 'Savings and debt goals with milestones and projected dates.' },
  { id: 'invest', label: 'Invest', to: '/invest', icon: LineChart, keys: 'g i', purpose: 'Holdings, trades, parcels, dividends and performance vs benchmarks.' },
  { id: 'plan', label: 'Plan', to: '/plan', icon: CalendarRange, badge: 'bills', purpose: 'Cashflow forecast, bills calendar, scenarios and calculators.' },
  { id: 'reports', label: 'Reports', to: '/reports', icon: PieChart, keys: 'g r', purpose: 'Net worth, spending, CGT, income, GST/BAS or VAT and more — exportable.' },
  { id: 'business', label: 'Business', to: '/business', icon: Briefcase, purpose: 'Customers, suppliers, invoices, bills and GST/VAT summaries.' },
];

export const SETTINGS_ITEM: NavItem = { id: 'settings', label: 'Settings', to: '/settings', icon: Settings, purpose: 'Profile, security, entity, members and data.' };
export const HELP_ICON = CircleHelp;
